import { useState } from "react";
import LightboxButton from "../../components/lightbox/LightboxButton";
import NoteField from "./NoteField";
import type { DossierNotes } from "./useDossierNotes";
import styles from "./PeopleMap.module.scss";

/* UI-DESIGN: 2026-10-25 (Taco picked C's people map inside B3's tabs: "really
   fun and visually amazing") your connections drawn around you. The API sends
   each relationship as one sentence, so the map pulls the person's name from
   it for the node and keeps the full sentence in the list underneath. Tense
   relationships (suspicious, estranged, rival...) get a red dashed line. */
const NAME = /\b([A-Z][a-z'’-]+(?:\s+[A-Z][a-z'’-]+)+)\b/g;
const TENSE = /suspicious|distrust|don'?t trust|rival|enemy|estranged|hate|resent|blackmail|threat/i;

type Person = { text: string; name: string; first: string; initials: string; tense: boolean };

const toPerson = (text: string): Person => {
  /* the last full name in the sentence is the person ("Confidante to Vivian
     Ashford", "Suspicious of the concierge, Helena Voss") */
  const names = [...text.matchAll(NAME)].map((m) => m[1]).filter((n) => !/^(The|A|An)\s/.test(n));
  const name = names.at(-1) ?? text.split(/[,(]/)[0].trim().split(/\s+/).slice(0, 2).join(" ");
  const parts = name.split(/\s+/);
  return {
    text,
    name,
    first: parts[0],
    initials: (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase(),
    tense: TENSE.test(text),
  };
};

const W = 360;
const C = W / 2;

const PeopleMap = ({ relationships, notes }: { relationships: string[]; notes: DossierNotes }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const people = relationships.map(toPerson);
  const selectedPerson = people.find(person => person.name === selected);
  const selectedNote = selectedPerson ? notes.notes.people[selectedPerson.name] : undefined;
  const n = people.length;
  const r = n <= 4 ? 112 : 128;
  /* four sit on the diagonals like the concept; other counts spread evenly from the top */
  const start = n === 4 ? -135 : -90;
  const nodes = people.map((p, i) => {
    const a = ((start + (360 / n) * i) * Math.PI) / 180;
    return { ...p, x: C + r * Math.cos(a), y: C + r * Math.sin(a) };
  });

  /* fit the drawing to the nodes and their names, no empty band above or below */
  const top = Math.min(C - 40, ...nodes.map((p) => p.y - 50));
  const bottom = Math.max(C + 40, ...nodes.map((p) => p.y + 50));
  return (
    <>
      <svg className={styles.map} viewBox={`0 ${top} ${W} ${bottom - top}`} role="group" aria-label={`You and ${n} people you know. Select a person to add notes.`}>
        {nodes.map((p) => {
          const dx = p.x - C, dy = p.y - C, len = Math.hypot(dx, dy) || 1;
          return (
            <line
              key={`l-${p.text}`}
              x1={C + (dx / len) * 34} y1={C + (dy / len) * 34}
              x2={p.x - (dx / len) * 24} y2={p.y - (dy / len) * 24}
              className={p.tense ? styles.tense : styles.line}
            />
          );
        })}
        <circle cx={C} cy={C} r={32} className={styles.you} />
        <text x={C} y={C + 5} className={styles.youLabel}>You</text>
        {nodes.map((p) => {
          const below = p.y > C + 1;
          return (
            <g key={p.text} role="button" tabIndex={0}
              aria-label={`Notes about ${p.name}${notes.notes.people[p.name]?.suspect ? ", marked as suspect" : ""}`}
              aria-pressed={selected === p.name} className={styles.mapButton}
              onClick={() => setSelected(p.name)}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelected(p.name); }
              }}>
              <circle cx={p.x} cy={p.y} r={30} fill="transparent" />
              {notes.notes.people[p.name]?.suspect && <ellipse cx={p.x} cy={p.y} rx={29} ry={26} className={styles.suspectRing} />}
              <circle cx={p.x} cy={p.y} r={22} className={styles.node} />
              <text x={p.x} y={p.y + 5} className={styles.initials}>{p.initials}</text>
              <text x={p.x} y={below ? p.y + 42 : p.y - 32} className={p.tense ? styles.nameTense : styles.name}>{p.first}</text>
            </g>
          );
        })}
      </svg>
      {selectedPerson ? <section className={styles.personNotes} aria-label={`Notes about ${selectedPerson.name}`}>
        <h3 className={styles.personTitle}>{selectedPerson.name}</h3>
        {/* UI-DESIGN: show the complete selected relationship beside its notes. */}
        <p className={styles.relationship}>{selectedPerson.text}</p>
        <LightboxButton tone="quiet" isDisabled={!notes.enabled}
          aria-pressed={selectedNote?.suspect ?? false}
          onPress={() => notes.setPerson(selectedPerson.name, { suspect: !selectedNote?.suspect })}>
          {selectedNote?.suspect ? "Unmark as suspect" : "Mark as suspect"}
        </LightboxButton>
        <NoteField quiet label={`My notes about ${selectedPerson.name}`} value={selectedNote?.text ?? ""}
          onChange={text => notes.setPerson(selectedPerson.name, { text })} notes={notes} />
      </section> : <p className={styles.hint}>Tap someone to keep notes or mark a suspect.</p>}
      <details className={styles.relationships}>
      <summary>All relationships</summary>
      <ul className={styles.list}>
        {people.map((p) => (
          <li key={p.text} className={styles.row}>
            <button type="button" className={styles.personButton} onClick={() => setSelected(p.name)}
              aria-pressed={selected === p.name}>
              <span className={notes.notes.people[p.name]?.suspect ? styles.badgeSuspect : p.tense ? styles.badgeTense : styles.badge} aria-hidden>{p.initials}</span>
              <span>{p.text}{notes.notes.people[p.name]?.suspect && <span className={styles.noteFlag}>Marked as suspect</span>}
                {notes.notes.people[p.name]?.text && <span className={styles.noteFlag}>Has notes</span>}</span>
            </button>
          </li>
        ))}
      </ul>
      </details>
    </>
  );
};

export default PeopleMap;
